import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, PackageCheck, UserCircle } from 'lucide-react';
import { typography } from '../../styles/typography';
import { PrimaryButton } from '../ui';
import LostAndFoundImageUpload from './LostAndFoundImageUpload';
import {
  LOST_FOUND_STATUS,
  getLocationFieldLabels,
  getStatusLabel,
} from './lostAndFoundData';
import {
  getLostFoundSiteId,
  lostAndFoundApi,
  setLostFoundSiteId,
} from './lostAndFoundApi';

const INPUT_CLASS =
  'w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-base text-black font-["Open_Sans"] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6BABA0]/40 focus:border-[#6BABA0]';

const LABEL_CLASS = 'block text-sm font-medium text-black mb-2 font-["Open_Sans"]';

const STATUS_STYLES = {
  [LOST_FOUND_STATUS.LOST]: 'bg-amber-50 text-amber-800 border-amber-200',
  [LOST_FOUND_STATUS.FOUND]: 'bg-blue-50 text-blue-800 border-blue-200',
  [LOST_FOUND_STATUS.COLLECTED]: 'bg-green-50 text-green-800 border-green-200',
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className={LABEL_CLASS}>{label}</label>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 className="text-sm font-semibold text-gray-800 font-['Open_Sans'] tracking-wide uppercase">
    {children}
  </h4>
);

const CollectItemPreview = ({ item }) => {
  if (!item) return null;
  const { locationLabel, handoffLabel } = getLocationFieldLabels(item.status);

  return (
    <div className="flex flex-col gap-5">
      <div className="relative w-full aspect-[4/3] max-h-56 rounded-2xl overflow-hidden border border-zinc-200 bg-gray-50 shadow-inner">
        {item.image_url ? (
          <img src={item.image_url} alt={item.item_name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400 font-['Open_Sans']">
            No image
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium text-gray-500 font-['Open_Sans'] uppercase tracking-wider">
          Item
        </p>
        <p className={`${typography.h4} text-black font-['Open_Sans'] leading-snug`}>
          {item.item_name}
        </p>
        <span
          className={`self-start inline-flex px-2.5 py-1 rounded-lg text-xs font-medium border font-['Open_Sans'] ${
            STATUS_STYLES[item.status] || 'bg-gray-50 text-gray-700 border-gray-200'
          }`}
        >
          {getStatusLabel(item.status)}
        </span>
      </div>

      <div className="flex flex-col gap-3 pt-1 border-t border-zinc-100">
        <div className="flex gap-3 items-start">
          <MapPin className="w-4 h-4 text-[#6BABA0] shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-xs text-gray-500 font-['Open_Sans']">{locationLabel}</p>
            <p className="text-sm text-black font-['Open_Sans']">{item.location || '—'}</p>
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <PackageCheck className="w-4 h-4 text-[#6BABA0] shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-xs text-gray-500 font-['Open_Sans']">{handoffLabel}</p>
            <p className="text-sm text-black font-['Open_Sans']">
              {item.submit_collect_at || '—'}
            </p>
          </div>
        </div>
        {item.description && (
          <p className="text-sm text-gray-600 font-['Open_Sans'] leading-relaxed border-t border-zinc-100 pt-3">
            {item.description}
          </p>
        )}
      </div>
    </div>
  );
};

const emptyForm = {
  person_name: '',
  mobile_number: '',
  block: '',
  flat: '',
  description: '',
};

const CollectLostAndFoundItem = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [effectiveSiteId, setEffectiveSiteId] = useState(() => getLostFoundSiteId());
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [blocksError, setBlocksError] = useState('');
  const [flatsError, setFlatsError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [blocks, setBlocks] = useState([]);
  const [flats, setFlats] = useState([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [loadingFlats, setLoadingFlats] = useState(false);

  const loadItem = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await lostAndFoundApi.getById(id, effectiveSiteId);
      setItem(data);
      if (data.site_id) {
        setEffectiveSiteId(data.site_id);
        setLostFoundSiteId(data.site_id);
      }
      if (data.status === LOST_FOUND_STATUS.COLLECTED) {
        navigate(`/lost-and-found/${id}`, { replace: true });
      } else if (data.status !== LOST_FOUND_STATUS.FOUND) {
        setError('Only found items can be collected.');
      }
    } catch (err) {
      setError(err.message || 'Failed to load item');
    } finally {
      setIsLoading(false);
    }
  }, [id, effectiveSiteId, navigate]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  const siteIdForApi = item?.site_id || effectiveSiteId;

  useEffect(() => {
    if (!siteIdForApi) return;
    let cancelled = false;
    const loadBlocks = async () => {
      setLoadingBlocks(true);
      setBlocksError('');
      try {
        const list = await lostAndFoundApi.getBlocks(siteIdForApi);
        if (!cancelled) {
          setBlocks(list);
          if (list.length === 0) {
            setBlocksError('No blocks found for this site.');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setBlocks([]);
          setBlocksError(err.message || 'Failed to load blocks');
        }
      } finally {
        if (!cancelled) setLoadingBlocks(false);
      }
    };
    loadBlocks();
    return () => {
      cancelled = true;
    };
  }, [siteIdForApi]);

  useEffect(() => {
    if (!siteIdForApi || !form.block) {
      setFlats([]);
      setFlatsError('');
      return;
    }
    let cancelled = false;
    const loadFlats = async () => {
      setLoadingFlats(true);
      setFlatsError('');
      try {
        const list = await lostAndFoundApi.getFlats(siteIdForApi, form.block);
        if (!cancelled) {
          setFlats(list);
          if (list.length === 0) {
            setFlatsError('No flats found for this block.');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setFlats([]);
          setFlatsError(err.message || 'Failed to load flats');
        }
      } finally {
        if (!cancelled) setLoadingFlats(false);
      }
    };
    loadFlats();
    return () => {
      cancelled = true;
    };
  }, [siteIdForApi, form.block]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleBlockChange = e => {
    const block = e.target.value;
    setForm(prev => ({ ...prev, block, flat: '' }));
  };

  const handlePhotoChange = dataUrl => {
    setPhotoPreview(dataUrl);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.person_name.trim()) {
      setError('Person name is required');
      return;
    }
    if (!form.mobile_number.trim()) {
      setError('Mobile number is required');
      return;
    }
    if (!form.block) {
      setError('Block is required');
      return;
    }
    if (!form.flat) {
      setError('Flat is required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const payload = {
        person_name: form.person_name,
        mobile_number: form.mobile_number,
        collector_block_id: form.block,
        collector_flat_id: form.flat,
        description: form.description,
      };
      if (photoFile) {
        payload.collector_photo_s3_key = await lostAndFoundApi.uploadCollectorPhoto(
          id,
          photoFile,
          siteIdForApi
        );
      }
      await lostAndFoundApi.collect(id, payload, siteIdForApi);
      navigate(`/lost-and-found/${id}`);
    } catch (err) {
      setError(err.message || 'Failed to collect item');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!siteIdForApi && !isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-amber-700 font-['Open_Sans']">
          Select a site on the Lost & Found list first.
        </p>
        <button
          type="button"
          onClick={() => navigate('/lost-and-found')}
          className="px-4 py-2 rounded-lg bg-[#6BABA0] text-white"
        >
          Go to list
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-[#6BABA0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !item) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,340px)_1fr] gap-6 items-start">
        <div className="p-5 md:p-6 lg:sticky lg:top-4">
          <p className="text-xs font-semibold text-[#6BABA0] font-['Open_Sans'] uppercase tracking-wider mb-4">
            Item details
          </p>
          <CollectItemPreview item={item} />
        </div>

        <div className="p-6 md:p-8 flex flex-col lg:pl-2">
          <div className="flex items-start gap-4 pb-6 border-b border-zinc-100">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#6BABA0]/15 text-[#6BABA0]">
              <UserCircle className="h-7 w-7" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <h3 className={`${typography.h3} text-black font-['Open_Sans']`}>Collect item</h3>
              <p className="text-sm text-gray-500 font-['Open_Sans'] mt-1 leading-relaxed">
                Record who collected this item and where they live.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 pt-6">
            <div className="flex flex-col gap-8">
              <section className="flex flex-col gap-4">
                <SectionTitle>Collector</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Person name">
                    <input
                      type="text"
                      value={form.person_name}
                      onChange={e => updateField('person_name', e.target.value)}
                      placeholder="Full name"
                      className={INPUT_CLASS}
                    />
                  </Field>
                  <Field label="Mobile number">
                    <input
                      type="tel"
                      value={form.mobile_number}
                      onChange={e => updateField('mobile_number', e.target.value)}
                      placeholder="e.g. +919999999999"
                      className={INPUT_CLASS}
                    />
                  </Field>
                </div>
              </section>

              <section className="flex flex-col gap-4">
                <SectionTitle>Photo & residence</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start">
                  <LostAndFoundImageUpload
                    label="Person photo"
                    value={photoPreview}
                    onChange={handlePhotoChange}
                    onFileSelect={setPhotoFile}
                    onValidationError={setError}
                    emptyTitle="Add photo"
                    emptyHint="Optional"
                    compact
                  />
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Block">
                        <select
                          value={form.block}
                          onChange={handleBlockChange}
                          disabled={loadingBlocks}
                          className={`${INPUT_CLASS} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                        >
                          <option value="">
                            {loadingBlocks ? 'Loading blocks…' : 'Select block'}
                          </option>
                          {blocks.map(block => (
                            <option key={block.id} value={block.id}>
                              {block.label}
                            </option>
                          ))}
                        </select>
                        {blocksError && (
                          <p className="mt-1 text-xs text-amber-700 font-['Open_Sans']">
                            {blocksError}
                          </p>
                        )}
                      </Field>
                      <Field label="Flat">
                        <select
                          value={form.flat}
                          onChange={e => updateField('flat', e.target.value)}
                          disabled={!form.block || loadingFlats}
                          className={`${INPUT_CLASS} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                        >
                          <option value="">
                            {!form.block
                              ? 'Select block first'
                              : loadingFlats
                                ? 'Loading flats…'
                                : 'Select flat'}
                          </option>
                          {flats.map(flat => (
                            <option key={flat.id} value={flat.id}>
                              {flat.label}
                            </option>
                          ))}
                        </select>
                        {flatsError && (
                          <p className="mt-1 text-xs text-amber-700 font-['Open_Sans']">
                            {flatsError}
                          </p>
                        )}
                      </Field>
                    </div>
                    <Field label="Notes">
                      <textarea
                        value={form.description}
                        onChange={e => updateField('description', e.target.value)}
                        placeholder="e.g. collected at gate 2, ID verified"
                        rows={3}
                        className={`${INPUT_CLASS} resize-none`}
                      />
                    </Field>
                  </div>
                </div>
              </section>
            </div>

            {error && (
              <p className="text-sm text-red-600 font-['Open_Sans'] mt-6 rounded-lg bg-red-50 border border-red-100 px-4 py-3">
                {error}
              </p>
            )}

            <div className="flex flex-wrap items-center justify-end gap-3 mt-8 pt-6 border-t border-zinc-100">
              <button
                type="button"
                onClick={() => navigate(`/lost-and-found/${id}`)}
                className="px-6 py-3 rounded-xl border border-zinc-300 text-black text-sm font-medium font-['Open_Sans'] hover:bg-gray-50 transition-colors min-w-[120px]"
              >
                Cancel
              </button>
              <PrimaryButton
                type="submit"
                disabled={isSubmitting || item?.status !== LOST_FOUND_STATUS.FOUND}
                className="!w-auto min-w-[180px] !h-12 !text-base !font-semibold !font-['Open_Sans'] !bg-emerald-600 hover:!bg-emerald-700"
              >
                {isSubmitting ? 'Collecting...' : 'Collect item'}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CollectLostAndFoundItem;
